import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase'

const AuthContext = createContext({})

export const useAuth = () => useContext(AuthContext)

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const userRef = useRef(user)

  const handleUserSession = useCallback(async (supabaseUser) => {
    try {
      console.log('Handling user session for:', supabaseUser.email, 'User ID:', supabaseUser.id)
      
      // Use Supabase user data directly for now
      const userData = {
        id: supabaseUser.id,
        email: supabaseUser.email,
        name: supabaseUser.user_metadata?.full_name || supabaseUser.email.split('@')[0]
      }
      
      console.log('Setting user data:', userData)
      setUser(userData)
      userRef.current = userData
      console.log('User session handled successfully')
      
    } catch (error) {
      console.error('Error handling user session:', error)
      // Fallback to basic user data
      const fallbackUser = {
        id: supabaseUser.id,
        email: supabaseUser.email,
        name: supabaseUser.user_metadata?.full_name || supabaseUser.email.split('@')[0]
      }
      console.log('Using fallback user due to error:', fallbackUser)
      setUser(fallbackUser)
    }
  }, [])

  useEffect(() => {
    let isMounted = true

    // Handle magic link authentication 
    const handleMagicLink = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search)
        const hashParams = new URLSearchParams(window.location.hash.substring(1))
        
        // Check for token-based magic link (new Supabase format)
        const token = urlParams.get('token')
        const type = urlParams.get('type')
        const errorParam = urlParams.get('error') || hashParams.get('error')
        const errorCode = urlParams.get('error_code') || hashParams.get('error_code')
        
        // Check for session-based magic link (hash-based format)
        const accessToken = hashParams.get('access_token') || urlParams.get('access_token')
        const refreshToken = hashParams.get('refresh_token') || urlParams.get('refresh_token')
        
        // Debug: Log the full URL and search params
        console.log('Full URL:', window.location.href)
        console.log('Search params:', window.location.search)
        console.log('Hash params:', window.location.hash)
        
        console.log('Magic link detection:', { token, type, accessToken, errorParam })
        
        // Handle authentication errors
        if (errorParam) {
          console.error('Magic link error:', { error: errorParam, errorCode })
          
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname)
          
          if (errorCode === 'otp_expired') {
            localStorage.setItem('authError', 'ログインリンクの有効期限が切れています。新しいリンクを送信してください。')
          } else if (errorParam === 'access_denied') {
            localStorage.setItem('authError', 'ログインがキャンセルされました。再度お試しください。')
          } else {
            localStorage.setItem('authError', 'ログインに失敗しました。再度お試しください。')
          }
          
          setLoading(false)
          return
        }
        
        // Handle token-based magic link verification
        if (token && type === 'magiclink') {
          console.log('Processing token-based magic link with token:', token)
          
          try {
            // For magic links, we need to exchange the token for a session
            const { data, error } = await supabase.auth.verifyOtp({
              token_hash: token,
              type: 'magiclink'
            })
            
            if (error) {
              console.error('Error verifying magic link token:', error)
              
              // Handle specific error types
              if (error.message.includes('expired') || error.message.includes('invalid')) {
                localStorage.setItem('authError', 'ログインリンクの有効期限が切れています。新しいリンクを送信してください。')
              } else {
                localStorage.setItem('authError', 'ログインに失敗しました。再度お試しください。')
              }
              
              // Clean up URL
              window.history.replaceState({}, document.title, window.location.pathname)
              setLoading(false)
              return
            }
            
            if (data?.user && data?.session) {
              console.log('Token-based magic link authentication successful for:', data.user.email)
              
              // Clean up URL first to prevent re-processing
              window.history.replaceState({}, document.title, window.location.pathname)
              
              // Set the session and handle user immediately
              await handleUserSession(data.user)
              return
            }
          } catch (verifyError) {
            console.error('Exception during magic link verification:', verifyError)
            localStorage.setItem('authError', 'ログインに失敗しました。再度お試しください。')
            window.history.replaceState({}, document.title, window.location.pathname)
            setLoading(false)
            return
          }
        }
        
        // Handle magic link verification from URL (when redirected from Supabase)
        if (window.location.pathname === '/dashboard' && (token || accessToken)) {
          console.log('Processing magic link from dashboard redirect')
          
          // If we have a token, try to verify it
          if (token && type === 'magiclink') {
            try {
              const { data, error } = await supabase.auth.verifyOtp({
                token_hash: token,
                type: 'magiclink'
              })
              
              if (!error && data?.user) {
                console.log('Magic link verification successful from redirect')
                await handleUserSession(data.user)
                window.history.replaceState({}, document.title, window.location.pathname)
                return
              }
            } catch (verifyError) {
              console.error('Error verifying magic link from redirect:', verifyError)
            }
          }
        }
        
        // Handle session-based magic link (legacy format)
        if (accessToken && refreshToken) {
          console.log('Processing session-based magic link with tokens')
          console.log('Access token length:', accessToken.length)
          console.log('Refresh token length:', refreshToken.length)
          
          try {
            console.log('About to call setSession...')
            
            // Try to get user directly from access token first
            const { data: userData, error: userError } = await supabase.auth.getUser(accessToken)
            console.log('Direct user lookup result:', { userData, userError })
            
            if (!userError && userData?.user) {
              console.log('Direct user lookup successful for:', userData.user.email)
              await handleUserSession(userData.user)
              
              // Clean up URL
              window.history.replaceState({}, document.title, window.location.pathname)
              return
            }
            
            // Fallback to setSession
            console.log('Falling back to setSession...')
            const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            })
            
            console.log('Session set result:', { sessionData, sessionError })
            
            if (sessionError) {
              console.error('Error setting session:', sessionError)
              localStorage.setItem('authError', 'ログインに失敗しました。再度お試しください。')
              setLoading(false)
              return
            }
            
            if (sessionData?.user) {
              console.log('Session-based magic link authentication successful for:', sessionData.user.email)
              await handleUserSession(sessionData.user)
              
              // Clean up URL
              window.history.replaceState({}, document.title, window.location.pathname)
              return
            } else {
              console.log('No user data in session response')
            }
          } catch (sessionException) {
            console.error('Exception during session setting:', sessionException)
            localStorage.setItem('authError', 'ログインに失敗しました。再度お試しください。')
            setLoading(false)
            return
          }
        }
        
        // Handle access token only (new Supabase format)
        if (accessToken && !refreshToken) {
          console.log('Processing access token only authentication')
          
          try {
            // Try to get session from the access token
            const { data: sessionData, error: sessionError } = await supabase.auth.getUser(accessToken)
            
            if (sessionError) {
              console.error('Error getting user from access token:', sessionError)
            } else if (sessionData?.user) {
              console.log('Access token authentication successful for:', sessionData.user.email)
              await handleUserSession(sessionData.user)
              
              // Clean up URL
              window.history.replaceState({}, document.title, window.location.pathname)
              return
            }
          } catch (tokenError) {
            console.error('Exception during access token processing:', tokenError)
          }
        }
        
        // Check for existing session
        const { data: sessionData } = await supabase.auth.getSession()
        if (sessionData?.session?.user) {
          console.log('Found existing session for:', sessionData.session.user.email)
          await handleUserSession(sessionData.session.user)
        } else {
          console.log('No existing session found')
        }
        
      } catch (error) {
        console.error('Error handling magic link:', error)
        localStorage.setItem('authError', 'ログインに失敗しました。再度お試しください。')
        setLoading(false)
      }
    }

    // Get initial session
    const getInitialSession = async () => {
      try {
        console.log('Getting initial session...')
        const { data: { session } } = await supabase.auth.getSession()
        if (isMounted && session?.user && (!userRef.current || userRef.current.email !== session.user.email)) {
          console.log('Found existing session for:', session.user.email)
          await handleUserSession(session.user)
        } else if (isMounted && !session?.user) {
          console.log('No existing session found')
        } else {
          console.log('User already exists, skipping initial session')
        }
      } catch (error) {
        console.error('Error getting initial session:', error)
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    // Handle magic link first, then get initial session
    handleMagicLink().then(() => {
      getInitialSession()
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state change:', event, session?.user?.email)
        
        if (!isMounted) return

        try {
          console.log('Auth state change event:', event, 'User:', session?.user?.email)
          
          if (event === 'SIGNED_IN' && session?.user) {
            console.log('User signed in:', session.user.email)
            // Only handle user session if we don't already have user data
            if (!userRef.current || userRef.current.email !== session.user.email) {
              await handleUserSession(session.user)
              console.log('User session handled after SIGNED_IN event')
            } else {
              console.log('User already exists, skipping session handling')
            }
          } else if (event === 'SIGNED_OUT') {
            console.log('User signed out')
            setUser(null)
          } else if (event === 'TOKEN_REFRESHED' && session?.user) {
            console.log('Token refreshed for user:', session.user.email)
            // トークン更新時は既存のユーザー情報を維持
            if (!userRef.current) {
              await handleUserSession(session.user)
            }
          } else if (session?.user && !userRef.current) {
            console.log('Session exists but no user state, setting user:', session.user.email)
            // セッションはあるがユーザー情報がない場合のみ処理
            await handleUserSession(session.user)
          } else if (!session?.user) {
            console.log('No session, clearing user')
            setUser(null)
          }
        } catch (error) {
          console.error('Error handling auth state change:', error)
          setUser(null)
        } finally {
          if (isMounted) {
            setLoading(false)
            console.log('Loading set to false in auth state change')
          }
        }
      }
    )

    return () => {
      isMounted = false
      subscription.unsubscribe()
    }
  }, [handleUserSession])

  const signIn = async (email) => {
    try {
      // Validate email format
      if (!email || !email.includes('@')) {
        throw new Error('有効なメールアドレスを入力してください')
      }

      // Send magic link for passwordless authentication
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`,
          shouldCreateUser: true
        }
      })

      if (error) {
        // Handle rate limiting error specifically
        if (error.message.includes('For security purposes') || error.status === 429) {
          throw new Error('セキュリティのため、しばらく時間をおいてから再度お試しください。（約1分後）')
        }
        throw error
      }

      return { user: null, error: null, message: '認証メールを送信しました。メールを確認してください。' }
    } catch (error) {
      console.error('Sign in error:', error)
      return { user: null, error: error.message }
    }
  }

  const signOut = async () => {
    try {
      await supabase.auth.signOut()
      setUser(null)
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  const value = {
    user,
    signIn,
    signOut,
    loading
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}